/**
 * Helpers for keeping human messaging isolated from AI agent threads/messages.
 */
import { isAiConversationType, isGroupConversationType } from '../constants/conversation';
import type { Conversation, ExtendedChatMessage } from '../types/chat';

/** True when a chat message was produced by an AI agent (not a human peer). */
export const isAiGeneratedChatMessage = (
  message:
    | (Pick<ExtendedChatMessage, 'type' | 'isAgentMessage' | 'system' | 'user'> & {
        user?: { _id?: string | number };
      })
    | null
    | undefined,
  aiAgentUserId?: number | string | null,
): boolean => {
  if (!message) return false;
  if (message.isAgentMessage === true) return true;
  const type = String(message.type || '').toLowerCase();
  if (type === 'airesponse' || type.includes('airesponse') || type === 'ai') {
    return true;
  }
  if (
    aiAgentUserId != null &&
    String(aiAgentUserId).trim() !== '' &&
    message.user?._id != null &&
    String(message.user._id) === String(aiAgentUserId)
  ) {
    return true;
  }
  return false;
};

/** True for standard human 1:1 DirectChat (not group, not AI agent thread). */
export const isHumanDirectConversation = (
  conversation: Pick<Conversation, 'category' | 'conversationType'> | null | undefined,
): boolean => {
  if (!conversation) return false;
  if (conversation.category === 'groups' || conversation.category === 'ai') {
    return false;
  }
  if (isGroupConversationType(conversation.conversationType)) return false;
  if (isAiConversationType(conversation.conversationType)) return false;
  return true;
};

/** Drop AI auto-replies from a human conversation message list. */
export const excludeAiGeneratedMessages = <T extends ExtendedChatMessage>(
  messages: T[],
  aiAgentUserId?: number | string | null,
): T[] => messages.filter(m => !isAiGeneratedChatMessage(m, aiAgentUserId));
